import { createRouter, createWebHistory } from 'vue-router'
import { practices } from '@/data/practices'

const SITE_URL = 'https://dyhanie-online.ru'
const DEFAULT_IMAGE = `${SITE_URL}/icons/icon_512.png`
const YANDEX_METRIKA_ID = 109329820
const CHUNK_RELOAD_KEY = 'dyhanie-online-chunk-reload-path'
let isInitialRoute = true
let chunkReloadedInMemory = false

const Home = () => import('../views/Home.vue')
const About = () => import('../views/About.vue')
const AnulomaViloma = () => import('../views/AnulomaViloma.vue')
const Breathing478 = () => import('../views/Breathing478.vue')
const BoxBreathing = () => import('../views/BoxBreathing.vue')
const CustomBreathing = () => import('../views/CustomBreathing.vue')
const Privacy = () => import('../views/Privacy.vue')
const InstallPwa = () => import('../views/InstallPwa.vue')

const homeMeta = {
  title: 'Тренажер дыхания онлайн - дыхание от стресса, сна и концентрации',
  description: 'Онлайн-тренажер дыхания: Анулома-вилома, техника 4-7-8, дыхание по квадрату и свой вариант для настройки вдоха, выдоха и задержек.',
  keywords: 'дыхание от стресса онлайн, техника дыхания, дыхание анулома-вилома, дыхание для сна, дыхание по квадрату, тренажер для дыхания'
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: homeMeta
  },
  {
    path: practices.anuloma.path,
    name: 'AnulomaViloma',
    component: AnulomaViloma,
    meta: {
      title: practices.anuloma.seoTitle,
      description: practices.anuloma.seoDescription,
      keywords: practices.anuloma.seoKeywords
    }
  },
  {
    path: practices.breathing478.path,
    name: 'Breathing478',
    component: Breathing478,
    meta: {
      title: practices.breathing478.seoTitle,
      description: practices.breathing478.seoDescription,
      keywords: practices.breathing478.seoKeywords
    }
  },
  {
    path: practices.box.path,
    name: 'BoxBreathing',
    component: BoxBreathing,
    meta: {
      title: practices.box.seoTitle,
      description: practices.box.seoDescription,
      keywords: practices.box.seoKeywords
    }
  },
  {
    path: practices.custom.path,
    name: 'CustomBreathing',
    component: CustomBreathing,
    meta: {
      title: practices.custom.seoTitle,
      description: practices.custom.seoDescription,
      keywords: practices.custom.seoKeywords
    }
  },
  {
    path: '/about',
    name: 'About',
    component: About,
    meta: {
      title: 'Описание дыхания Анулома-вилома',
      description: 'Справочная страница о ступенях освоения дыхания Анулома-вилома, рекомендациях и безопасной практике.',
      keywords: practices.anuloma.seoKeywords
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: Privacy,
    meta: {
      title: 'Политика конфиденциальности - Дыхание онлайн',
      description: 'Политика конфиденциальности сайта Дыхание онлайн: localStorage, cookies, Яндекс Метрика и контакт для вопросов.',
      keywords: 'политика конфиденциальности, дыхание онлайн, яндекс метрика, cookies, localStorage'
    }
  },
  {
    path: '/install',
    name: 'InstallPwa',
    component: InstallPwa,
    meta: {
      title: 'Как установить PWA Дыхание онлайн на телефон',
      description: 'Инструкция, как добавить PWA-приложение Дыхание онлайн на экран iPhone, iPad или Android-смартфона.',
      keywords: 'pwa приложение, добавить сайт на экран, установить дыхание онлайн, iphone, android'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 }
  }
})

function setMetaTag(attribute, key, content) {
  if (!content) return
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function setCanonical(to) {
  let element = document.head.querySelector('link[rel="canonical"]')
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', 'canonical')
    document.head.appendChild(element)
  }
  element.setAttribute('href', `${SITE_URL}${to.path}`)
}

function trackYandexRoute(to, from, title) {
  if (isInitialRoute) {
    isInitialRoute = false
    return
  }

  if (typeof window.ym !== 'function') return

  const url = `${SITE_URL}${to.fullPath}`
  const referer = from && from.fullPath ? `${SITE_URL}${from.fullPath}` : document.referrer
  window.ym(YANDEX_METRIKA_ID, 'hit', url, {
    title,
    referer
  })
}

function isChunkLoadError(error) {
  const message = String((error && error.message) || error || '')
  return /Loading chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Load failed/i.test(message)
}

function reloadAfterChunkError() {
  if (typeof window === 'undefined') return
  if (chunkReloadedInMemory) return

  chunkReloadedInMemory = true

  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === path) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, path)
  } catch (error) {
    // Reload once even if storage is blocked.
  }

  window.location.reload()
}

router.onError((error) => {
  if (isChunkLoadError(error)) {
    reloadAfterChunkError()
  }
})

router.afterEach((to, from) => {
  const meta = to.meta || homeMeta
  const title = meta.title || homeMeta.title
  const description = meta.description || homeMeta.description
  const url = `${SITE_URL}${to.path}`

  document.title = title
  setMetaTag('name', 'description', description)
  setMetaTag('name', 'keywords', meta.keywords || homeMeta.keywords)
  setMetaTag('name', 'robots', 'index,follow')
  setMetaTag('property', 'og:locale', 'ru_RU')
  setMetaTag('property', 'og:type', 'website')
  setMetaTag('property', 'og:site_name', 'Дыхание онлайн')
  setMetaTag('property', 'og:title', title)
  setMetaTag('property', 'og:description', description)
  setMetaTag('property', 'og:url', url)
  setMetaTag('property', 'og:image', DEFAULT_IMAGE)
  setMetaTag('name', 'twitter:card', 'summary')
  setMetaTag('name', 'twitter:title', title)
  setMetaTag('name', 'twitter:description', description)
  setMetaTag('name', 'twitter:image', DEFAULT_IMAGE)
  setCanonical(to)
  trackYandexRoute(to, from, title)

  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch (error) {
    // Ignore unavailable storage in private or restricted modes.
  }
})

export default router
